import { LocalizedLink as Link } from "@/components/routing/LocalizedLink";
import { useTranslation } from "react-i18next";
import { ArrowRight, Briefcase, Users, Repeat, UserCog, Check } from "lucide-react";
import { PublicShell } from "@/components/layouts/PublicShell";
import { Seo } from "@/components/seo/Seo";
import { Button } from "@/components/ui/button";
import { PRICING_CATALOG } from "@/content/pricingCatalog";
import { SmartImage } from "@/components/SmartImage";
import coachingCover from "@/assets/services/coaching-cover.jpg";
import mentoringCover from "@/assets/services/mentoring-cover.jpg";
import fractionalCover from "@/assets/services/fractional-cover.jpg";
import microservicesCover from "@/assets/services/microservices-cover.jpg";

type CardId = "coaching" | "mentoring" | "fractional" | "microservices";

interface CardSpec {
  id: CardId;
  to: string;
  ctaTo: string;
  Icon: typeof Users;
  priceEur: number;
  fromPrefix: boolean;
  unit?: string;
  cover: string;
}

const CARDS: CardSpec[] = [
  {
    id: "coaching",
    to: "/services/coaching",
    ctaTo: "/marketplace",
    Icon: Users,
    priceEur: PRICING_CATALOG.coach4.eur,
    fromPrefix: true,
    unit: "/ mo",
    cover: coachingCover,
  },
  {
    id: "mentoring",
    to: "/mentoring",
    ctaTo: "/mentoring",
    Icon: Repeat,
    priceEur: PRICING_CATALOG.founderM.eur,
    fromPrefix: true,
    unit: "/ mo",
    cover: mentoringCover,
  },
  {
    id: "fractional",
    to: "/fractional",
    ctaTo: "/teams",
    Icon: UserCog,
    priceEur: PRICING_CATALOG.fractional.eur,
    fromPrefix: true,
    unit: "/ mo",
    cover: fractionalCover,
  },
  {
    id: "microservices",
    to: "/services/microservices",
    ctaTo: "/services/microservices",
    Icon: Briefcase,
    priceEur: PRICING_CATALOG.microservice.eur,
    fromPrefix: true,
    cover: microservicesCover,
  },
];

function formatEUR(amount: number, locale: string): string {
  try {
    return new Intl.NumberFormat(locale || "en", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `€${amount}`;
  }
}

export default function ServicesIndex() {
  const { t, i18n } = useTranslation();
  return (
    <PublicShell>
      <Seo title={t("services.index.seoTitle")} description={t("services.index.seoDescription")} />
      <section className="container py-12 md:py-16">
        <div className="mx-auto mb-10 max-w-2xl text-center">
          <h1 className="font-display text-3xl font-bold tracking-tight md:text-5xl">{t("services.index.title")}</h1>
          <p className="mt-3 text-muted-foreground">{t("services.index.subtitle")}</p>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {CARDS.map((c, idx) => {
            const bullets = t(`services.index.cards.${c.id}.bullets`, {
              returnObjects: true,
              defaultValue: [],
            }) as string[];
            const ctaLabel = t(`services.index.cards.${c.id}.cta`);
            return (
              <article
                key={c.id}
                className="group flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card transition-all hover:-translate-y-0.5 hover:border-primary/60 hover:shadow-lg"
              >
                <Link to={c.to} className="relative block aspect-[16/10] overflow-hidden bg-muted">
                  <SmartImage
                    src={c.cover}
                    alt={t(`pages.navServices.${c.id}`)}
                    priority={idx === 0}
                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                  />
                  <div className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-background/90 px-2.5 py-1 text-xs font-semibold text-foreground shadow-sm backdrop-blur">
                    <c.Icon size={14} className="text-primary" />
                    {t(`pages.navServices.${c.id}`)}
                  </div>
                </Link>

                <div className="flex flex-1 flex-col gap-3 p-5">
                  <Link to={c.to} className="text-base font-semibold text-foreground hover:text-primary">
                    {t(`pages.navServices.${c.id}`)}
                  </Link>
                  <p className="text-sm text-muted-foreground">{t(`pages.navServices.${c.id}Desc`)}</p>

                  {Array.isArray(bullets) && bullets.length > 0 && (
                    <ul className="space-y-1.5 text-sm text-muted-foreground">
                      {bullets.map((b, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <Check size={14} className="mt-0.5 shrink-0 text-primary" />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  <div className="mt-auto flex items-end justify-between border-t border-border pt-4">
                    <div>
                      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{c.fromPrefix ? t("services.index.fromLabel") : ""}</div>
                      <div className="text-base font-bold text-foreground">{formatEUR(c.priceEur, i18n.language)}<span className="text-xs font-medium text-muted-foreground">{c.unit ?? ""}</span></div>
                    </div>
                    <Button asChild size="sm">
                      <Link to={c.ctaTo}>
                        {ctaLabel}
                        <ArrowRight size={14} className="ml-1.5" />
                      </Link>
                    </Button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </section>
    </PublicShell>
  );
}
